import React from "react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { formatPrice } from "@/src/utils/formatPrice";

import {
  offerUserStatusOptions,
  pricingPlanColumn,
  userNameColumn,
} from "./offerUserColumns";
import { getOfferDescription } from "../../../utils/data/Offers";
import PaymentIcon from "../../analytics/transactionAnalytics/tableParams/PaymentIcon";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import { dateColumn, statusColumn } from "../../dataTable/dataTableColumns";
import { getPlanDisplayedRentalDuration } from "../offerPlanView";

import type { TFunction } from "i18next";
import type { JSX } from "react";

import type { OfferViewRow } from "./offerViewColumns.types";
import type { OfferUserStat } from "../../../api/StatsAPI.types";
import type { OfferPlanTypeValue } from "../../../models/OfferPlan.types";
import type { PaymentKind } from "../../analytics/transactionAnalytics/tableParams/paymentIcons.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface OfferViewColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  offerPlanType: OfferPlanTypeValue;
}

// Les processeurs d'Okast et les cadeaux passent par Stripe.
const PAYMENT_KINDS: Record<string, PaymentKind> = {
  apple: "apple",
  google: "google",
  stripe_purchase: "stripe",
  okast_free: "stripe",
  stripe_subscription: "stripe",
  stripe_gift: "stripe",
};

export function offerViewRow(plan: OfferUserStat, t: TFunction): OfferViewRow {
  const { price, currency, user } = plan;
  const priceLabel = formatPrice(price, currency);

  return {
    uuid: plan.uuid,
    user_uuid: user.uuid,
    status: plan.status,
    user_name: `${user.first_name} ${user.last_name}`,
    pricing_plan: getOfferDescription(t, plan),
    price_label: priceLabel ?? "",
    price: priceLabel ? Number(price) : null,
    payment_processor: plan.payment_processor ?? "",
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- ?? would keep an empty date
    subscription_date: plan.subscription_date || plan.purchase_date || null,
    next_billing_date: plan.next_billing_date ?? null,
    rental_duration: getPlanDisplayedRentalDuration({
      limit_date: plan.limit_date,
      rental_duration: plan.rental_duration ?? undefined,
    }),
  };
}

interface PaymentProcessorCellProps {
  processor: string;
  t: TFunction;
}

function PaymentProcessorCell({
  processor,
  t,
}: PaymentProcessorCellProps): JSX.Element | null {
  const kind = PAYMENT_KINDS[processor];
  if (!kind) return null;

  const label = t(`editor-analytics:${kind}`);
  return (
    <span
      className="inline-flex align-middle"
      data-tooltip-content={label}
      data-tooltip-id={APP_TOOLTIP_ID}
    >
      <PaymentIcon kind={kind} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function offerViewColumns({
  t,
  projectSlug,
  offerPlanType,
}: OfferViewColumnsOptions): DataTableColumnDef<OfferViewRow>[] {
  const isSvod = offerPlanType === "svod";
  const priceTitle = t("editor-offers:price");
  const platformTitle = t("editor-offers:platform");
  const durationTitle = t("editor-offers:rental_duration");

  return [
    statusColumn<OfferViewRow>(
      "status",
      t("editor-offers:status"),
      offerUserStatusOptions(t),
    ),
    userNameColumn<OfferViewRow>(t, projectSlug),
    pricingPlanColumn<OfferViewRow>(t),
    {
      // L'identifiant reste la clé d'URL ; le tri porte sur le montant, pas sur le texte.
      id: "price_label",
      accessorFn: (row) => row.price,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={priceTitle} />
      ),
      meta: { label: priceTitle, width: "9%", minWidth: 110 },
      cell: ({ row }) => row.original.price_label,
    },
    {
      accessorKey: "payment_processor",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={platformTitle} />
      ),
      enableSorting: false,
      meta: {
        label: platformTitle,
        width: "9%",
        minWidth: 105,
        align: "center",
      },
      cell: ({ row }) => (
        <PaymentProcessorCell
          processor={row.original.payment_processor}
          t={t}
        />
      ),
    },
    dateColumn<OfferViewRow>(
      "subscription_date",
      t(`editor-offers:start_date_${offerPlanType}`),
      isSvod ? { width: "19%", minWidth: 235 } : { minWidth: 150 },
    ),
    isSvod
      ? // Dernière colonne (+8 px) : « Prochain prélèvement » triable.
        dateColumn<OfferViewRow>(
          "next_billing_date",
          t("editor-offers:next_billing"),
          { width: "18%", minWidth: 220 },
        )
      : {
          accessorKey: "rental_duration",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={durationTitle} />
          ),
          enableSorting: false,
          meta: { label: durationTitle, width: "15%", minWidth: 110 },
          cell: ({ row }) => row.original.rental_duration,
        },
  ];
}
